/**
 * La série : six phases, onze épisodes (document « AICECAP, Fil rouge »).
 *
 * Les titres, les phases et l'ordre viennent du fil rouge et font foi. Les
 * dates, durées, chiffres et le corps de l'épisode 03 sont les EXEMPLES de la
 * maquette : ils sont à remplacer par les vrais avant publication. Rien ne doit
 * être publié qui n'ait tourné.
 */

export type Phase = {
  id: string;
  /** Numéro sur deux chiffres. */
  num: string;
  title: string;
  /** Les outils de la phase, pour la bande « Le fil rouge » de l'accueil. */
  tools: string;
  /** Ce que le pipeline gagne à la fin de la phase. */
  gain: string;
};

export const phases: Phase[] = [
  { id: 'cerveau', num: '01', title: 'Le cerveau', tools: 'Prompt engineering, Projects, API, Tool Use.', gain: "v0 : un script qui résume des flux via l'API" },
  { id: 'outils', num: '02', title: 'Les outils Anthropic', tools: 'Claude Code, MCP, Agent SDK.', gain: 'Le script devient un repo propre, sources branchées en MCP' },
  { id: 'orchestration', num: '03', title: "L'orchestration", tools: 'n8n auto-hébergé, puis CrewAI.', gain: 'Exécution planifiée chaque semaine, trio chercheur, rédacteur, critique' },
  { id: 'memoire', num: '04', title: 'La mémoire', tools: 'RAG local, Chroma ou pgvector.', gain: "Le pipeline se souvient de ce qu'il a déjà couvert" },
  { id: 'souverainete', num: '05', title: 'La souveraineté', tools: 'Hermes et Llama contre Claude.', gain: 'Choix du modèle par tâche, sur chiffres' },
  { id: 'securite', num: '06', title: 'La sécurité', tools: 'OpenClaw, DNS, segmentation.', gain: 'Un agent personnel qui pilote le pipeline, sous contrôle' },
];

/* ---------------------------------------------------------------------------
   Corps d'article : le format carottage du fil rouge, en cinq sections fixes.
   --------------------------------------------------------------------------- */

/** Un paragraphe accepte du `code` en ligne, entre accents graves. */
export type Block =
  | { type: 'p'; text: string }
  /** Bloc de commandes. Les commentaires `#` et le mot-clé `export` sont colorés. */
  | { type: 'code'; code: string }
  | { type: 'video'; label: string; caption: string }
  | {
      type: 'table';
      head: string[];
      /** Première cellule : libellé ; les suivantes : valeurs mesurées. */
      rows: { cells: string[]; highlight?: boolean }[];
    }
  /** Encadré « ce qui casse » : une panne datée. */
  | { type: 'panne'; label: string; text: string };

export type Verdict = {
  bien: string;
  casse: string;
  client: string;
};

export type Article = {
  /** Les quatre premières sections ; le verdict vient de `verdict`. */
  objectif: Block[];
  installe: Block[];
  mesure: Block[];
  casse: Block[];
  verdict: Verdict;
};

export type Episode = {
  num: string;
  slug: string;
  title: string;
  /** Titre de la page d'article ; la partie après `|` est le mot d'accent. */
  headline?: string;
  phase: Phase['id'];
  /** `publie` : en ligne. `a-venir` : annoncé, sans lien. */
  status: 'publie' | 'a-venir';
  /** Date de publication ISO, ou mois annoncé (« février ») si à venir. */
  date?: string;
  planned?: string;
  lead?: string;
  /** Résumé court, pour les listes. */
  summary?: string;
  readingTime?: number;
  /** Durée de la vidéo, en minutes. */
  videoMinutes?: number;
  testedWith?: string;
  repo?: string;
  article?: Article;
};

export const episodes: Episode[] = [
  {
    num: '01',
    slug: 'univers-anthropic',
    title: "L'univers Anthropic, cartographié",
    phase: 'cerveau',
    status: 'publie',
    date: '2026-11-12',
    summary: 'Modèles, Projects, API, Claude Code, MCP : ce que chaque produit fait, pour qui, à quel coût.',
    videoMinutes: 9,
  },
  {
    num: '02',
    slug: 'prompt-engineering',
    title: 'Prompt engineering : ce qui se mesure',
    phase: 'cerveau',
    status: 'publie',
    date: '2026-12-10',
    summary: 'Quand la structure améliore le résultat, quand elle coûte des tokens pour rien.',
    videoMinutes: 7,
  },
  {
    num: '03',
    slug: 'pipeline-v0',
    title: "Pipeline v0 par l'API",
    headline: "Pipeline v0 |par l'API.",
    phase: 'cerveau',
    status: 'publie',
    date: '2027-01-14',
    lead: "Un script Python qui lit une trentaine de flux, les résume avec l'API Anthropic et produit le premier brouillon du Relevé. Ce qu'il coûte, et ce qu'il rate.",
    summary: "Un script Python qui résume une trentaine de flux avec l'API Anthropic. Coût par exécution relevé, pannes comprises.",
    readingTime: 12,
    videoMinutes: 8,
    testedWith: 'Python 3.12, SDK anthropic',
    repo: 'aicecap/phase-1',
    article: {
      objectif: [
        {
          type: 'p',
          text: "Un brouillon Markdown du Relevé hebdomadaire, classé par pilier, avec trois à cinq lignes par élément. Rien de plus : pas de planification, pas de mémoire, pas d'interface. On veut savoir si le résumé tient la route avant d'investir dans l'orchestration.",
        },
        {
          type: 'p',
          text: 'Entrée : flux RSS, avis de sécurité, notes de version et dépôts GitHub suivis. Sortie : un fichier `releve-2027-w02.md` relu à la main avant publication.',
        },
      ],
      installe: [
        {
          type: 'p',
          text: "Un environnement Python isolé dans le labo, une clé d'API dédiée avec plafond de dépenses, et un seul outil déclaré au modèle : `classer_element`, qui force une réponse structurée.",
        },
        {
          type: 'code',
          code: [
            "# labo, segment veille : aucune ressource de l'employeur",
            'python -m venv .venv && source .venv/bin/activate',
            'pip install anthropic feedparser',
            'export ANTHROPIC_API_KEY=…   # clé dédiée, plafond 10 $ par mois',
            'python pipeline.py --sources sources.yaml --out releve.md',
          ].join('\n'),
        },
        {
          type: 'video',
          label: 'Démo, 8 min, capture brute',
          caption: 'Lancement du script sur les 31 sources de la semaine 02, de la collecte au fichier Markdown.',
        },
      ],
      mesure: [
        {
          type: 'p',
          text: "Trois exécutions sur les mêmes sources, la même semaine. Les tokens sont relevés depuis la réponse de l'API, la durée avec `time`.",
        },
        {
          type: 'table',
          head: ['Mesure', 'Exéc. 1', 'Exéc. 2', 'Exéc. 3'],
          rows: [
            { cells: ['Éléments collectés', '148', '148', '148'] },
            { cells: ['Éléments retenus', '22', '24', '21'] },
            { cells: ['Durée', '3 min 52', '4 min 08', '3 min 47'] },
            { cells: ['Coût par exécution', '0,31 $', '0,34 $', '0,30 $'], highlight: true },
          ],
        },
      ],
      casse: [
        {
          type: 'panne',
          label: 'Panne, exécution 2',
          text: "Un même avis de sécurité publié par trois sources produit trois résumés. Sans mémoire de ce qui a déjà été vu, le pipeline ne peut pas dédupliquer : c'est le sujet de l'épisode 8.",
        },
        {
          type: 'p',
          text: 'Deuxième limite : le classement par pilier hésite entre « sécurité opérationnelle » et « infrastructure et réseau » pour tout ce qui touche au DNS. Ajouter deux exemples dans le prompt règle la moitié des cas.',
        },
      ],
      verdict: {
        bien: 'Un brouillon lisible en quatre minutes, pour une trentaine de centimes.',
        casse: 'Les doublons entre sources, et le classement des sujets réseau.',
        client: "Pas encore. Sans planification ni mémoire, c'est un script, pas un outil.",
      },
    },
  },
  { num: '04', slug: 'claude-code', title: 'Claude Code sur un vrai repo', phase: 'outils', status: 'a-venir', planned: 'février' },
  { num: '05', slug: 'mcp', title: "MCP et sa surface d'attaque", phase: 'outils', status: 'a-venir', planned: 'mars' },
  { num: '06', slug: 'n8n', title: 'n8n auto-hébergé', phase: 'orchestration', status: 'a-venir', planned: 'avril' },
  { num: '07', slug: 'trois-agents', title: 'Trois agents, deux frameworks', phase: 'orchestration', status: 'a-venir', planned: 'mai' },
  { num: '08', slug: 'rag-local', title: 'RAG local', phase: 'memoire', status: 'a-venir', planned: 'juin' },
  { num: '09', slug: 'modeles-locaux', title: 'Hermes et Llama contre Claude', phase: 'souverainete', status: 'a-venir', planned: 'juillet' },
  { num: '10', slug: 'openclaw', title: 'OpenClaw sous contrôle', phase: 'securite', status: 'a-venir', planned: 'août' },
  { num: '11', slug: 'trafic-sortant', title: 'Ce que mes agents appellent sur Internet', phase: 'securite', status: 'a-venir', planned: 'septembre' },
];

export const published = episodes.filter((episode) => episode.status === 'publie');

/** Le dernier épisode publié, s'il en existe un. */
export const latestEpisode: Episode | undefined = [...published].sort((a, b) =>
  (b.date ?? '').localeCompare(a.date ?? ''),
)[0];

export const findEpisode = (slug: string) => episodes.find((episode) => episode.slug === slug);

export const findPhase = (id: string) => phases.find((phase) => phase.id === id);

export const episodePath = (episode: Episode) => `/serie/${episode.slug}`;

/** Épisodes d'une phase, et combien sont publiés : les barres de progression. */
export const phaseEpisodes = (phaseId: string) =>
  episodes.filter((episode) => episode.phase === phaseId);
